/**
 * Zone-Based Literary Group Operations Hook
 * 
 * This hook provides literary group-related operations using the zone-based architecture.
 * It leverages the shared data zone for literary group management while maintaining
 * backward compatibility.
 * 
 * Migration from: useLitGroupOperations.js
 * Part of: Full Zone Migration
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSharedData } from '../core/useZoneHooks';
import { apiClient } from '../../../apiTools/apiClient';
import {
  fetchLitGroups as fetchLitGroupsThunk,
  fetchLitGroupProfile,
  setLitGroupSelection as setLitGroupSelectionAction,
  createLitGroup as createLitGroupThunk,
  updateLitGroup as updateLitGroupThunk
} from '../../slices/bookWorksheetDataSlice';

export const useLitGroupOperationsZoneBased = () => {
  const dispatch = useDispatch();
  const sharedData = useSharedData();

  // Extract relevant data from zone-based state with defensive defaults
  const {
    litGroups = [],
    litGroupProfiles = {},
    selections = {},
    loading = {},
    errors = {},
    lastUpdated = {},
    actions = {}
  } = sharedData || {};

  const { litGroupId: selectedLitGroupId } = selections;

  // Find selected litGroup from array
  const selectedLitGroup = useMemo(() =>
    litGroups.find(lg => lg.uuid === selectedLitGroupId) || null,
    [litGroups, selectedLitGroupId]
  );

  // Get selected litGroup profile if available
  const selectedLitGroupProfile = useMemo(() =>
    selectedLitGroupId ? litGroupProfiles[selectedLitGroupId] || null : null,
    [litGroupProfiles, selectedLitGroupId]
  );

  // Loading/error states
  const litGroupsLoading = loading.litGroups || false;
  const litGroupsError = errors.litGroups || null;
  const litGroupsLastUpdated = lastUpdated.litGroups || null;

  // Cache duration (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Operations with zone-based architecture
  const fetchLitGroups = useCallback(async (forceRefresh = false) => {
    // Check cache
    const now = Date.now();
    if (!forceRefresh && litGroupsLastUpdated && (now - litGroupsLastUpdated < CACHE_DURATION)) {
      if (litGroups.length > 0) {
        console.log('[useLitGroupOperationsZoneBased] Using cached lit groups');
        return Promise.resolve({ type: 'cache/hit', data: litGroups });
      }
    }

    console.log('[useLitGroupOperationsZoneBased] Loading literary groups');
    return actions.fetchLitGroups ? actions.fetchLitGroups() : dispatch(fetchLitGroupsThunk()).unwrap();
  }, [actions, dispatch, litGroups, litGroupsLastUpdated, CACHE_DURATION]);

  const fetchLitGroup = useCallback(async (litGroupId) => {
    if (!litGroupId) {
      console.error('[useLitGroupOperationsZoneBased] No litGroupId provided');
      return Promise.reject(new Error('No literary group ID provided'));
    }

    // Check if we already have this litGroup's data
    const existingLitGroup = litGroups.find(lg => lg.uuid === litGroupId);
    const existingProfile = litGroupProfiles[litGroupId];

    if (existingLitGroup && existingProfile) {
      console.log('[useLitGroupOperationsZoneBased] LitGroup already loaded:', litGroupId);
      return Promise.resolve({ type: 'cache/hit', data: existingLitGroup });
    }

    console.log('[useLitGroupOperationsZoneBased] Loading lit group profile:', litGroupId);
    return actions.fetchLitGroupProfile ? actions.fetchLitGroupProfile(litGroupId) : dispatch(fetchLitGroupProfile(litGroupId)).unwrap();
  }, [dispatch, litGroups, litGroupProfiles, actions]);

  const selectLitGroup = useCallback((litGroupId) => {
    console.log('[useLitGroupOperationsZoneBased] Selecting lit group:', litGroupId);
    if (actions.setLitGroupSelection) {
      actions.setLitGroupSelection(litGroupId);
    } else {
      dispatch(setLitGroupSelectionAction(litGroupId));
    }
  }, [dispatch, actions]);

  const setLitGroupSelection = selectLitGroup; // Alias for backward compatibility

  const createLitGroup = useCallback(async (data, litGroupIdParam) => {
    console.log('[useLitGroupOperationsZoneBased] Creating literary group with data wrapper');
    try {
      // Support both single argument and two argument calls
      let payload = data;

      // If litGroupIdParam is provided, add it to the data
      if (litGroupIdParam) {
        payload = { ...data, uuid: litGroupIdParam };
      }

      const result = await dispatch(createLitGroupThunk(payload)).unwrap();
      console.log('[useLitGroupOperationsZoneBased] Literary group created successfully:', result);
      return result;
    } catch (error) {
      console.error('[useLitGroupOperationsZoneBased] Error creating literary group:', error);
      throw error;
    }
  }, [dispatch]);

  const updateLitGroup = useCallback(async (litGroupIdOrData, dataParam) => {
    // Handle both object style { litGroupId, data } and separate arguments (litGroupId, data)
    let litGroupId, data;

    if (typeof litGroupIdOrData === 'object' && litGroupIdOrData.litGroupId) {
      // Object style: { litGroupId, data }
      litGroupId = litGroupIdOrData.litGroupId;
      data = litGroupIdOrData.data;
    } else {
      // Separate arguments style: (litGroupId, data)
      litGroupId = litGroupIdOrData;
      data = dataParam;
    }

    if (!litGroupId) {
      throw new Error('No literary group ID provided for update');
    }

    console.log('[useLitGroupOperationsZoneBased] Updating lit group with data wrapper:', litGroupId);
    try {
      const result = await dispatch(updateLitGroupThunk({
        litGroupUuid: litGroupId,
        profileData: data
      })).unwrap();
      console.log('[useLitGroupOperationsZoneBased] Literary group updated successfully:', result);
      return result;
    } catch (error) {
      console.error('[useLitGroupOperationsZoneBased] Error updating literary group:', error);
      throw error;
    }
  }, [dispatch]);

  const deleteLitGroup = useCallback(async (litGroupId, deleteType = 'soft') => {
    if (!litGroupId) {
      throw new Error('No literary group ID provided for deletion');
    }
    console.log('[useLitGroupOperationsZoneBased] Deleting lit group:', litGroupId, 'type:', deleteType);

    try {
      // Build query params based on delete type
      // Server unified delete endpoint: DELETE /api/delete/lit-group/:entityId
      const params = new URLSearchParams();
      params.set('deleteType', deleteType); // Critical: tells server which delete handler to use
      if (deleteType === 'hard') {
        params.set('cleanup', 'cleanup');
        params.set('cascade', 'cascade');
        params.set('force', 'true');
      } else if (deleteType === 'archive') {
        params.set('cleanup', 'no-cleanup');
        params.set('cascade', 'no-cascade');
      } else {
        // soft delete (default)
        params.set('cleanup', 'no-cleanup');
        params.set('cascade', 'cascade');
      }

      const queryString = params.toString();
      const url = `/delete/lit-group/${litGroupId}${queryString ? `?${queryString}` : ''}`;

      // A HARD CASCADE OUTLIVES THE DEFAULT 30s CEILING, ROUTINELY.
      //
      // Deleting one group runs its books through the full deleteBook cascade — characters with
      // their variations, speech samples and snippets, dialogue/rendered leaves, two S3 roots, the
      // group's own designs and preview objects, and ElevenLabs voice reclamation. Measured groups
      // here carry 1,750 speech samples and 100+ designs; that is minutes, not seconds.
      //
      // On timeout axios throws, so `fetchLitGroups(true)` below never runs: the operator is shown
      // a failure for a delete that actually SUCCEEDED, and the list still displays the group that
      // is already gone. That is the worst possible reading of a destructive action.
      //
      // Bounded rather than `timeout: 0` — there is no idle timer governing this request, so an
      // unbounded one would hang the UI silently if the server genuinely died mid-cascade.
      // apiClient.delete is (url, params, config) — the timeout belongs in the THIRD argument.
      // Passing it second sends it to substituteParams and silently leaves the 30s ceiling in place.
      const response = await apiClient.delete(url, {}, { timeout: 900000 });
      console.log('[useLitGroupOperationsZoneBased] Literary group deleted successfully:', response.data);

      // Refresh the litGroups list to reflect the deletion
      await fetchLitGroups(true);

      return response.data;
    } catch (error) {
      console.error('[useLitGroupOperationsZoneBased] Error deleting literary group:', error);
      throw error;
    }
  }, [fetchLitGroups]);

  const getDeleteImpact = useCallback((litGroupId) => {
    // Calculate impact of deleting a literary group
    // Must return a Promise because DeleteLitGroupModal calls .then() on the result
    const litGroup = litGroups.find(lg => lg.uuid === litGroupId);
    if (!litGroup) {
      return Promise.resolve({ books: 0, characters: 0, variations: 0 });
    }

    // Count books in this litGroup
    const books = (sharedData?.books || []).filter(book => book.litGroupId === litGroupId);

    return Promise.resolve({
      litGroupName: litGroup.name || 'Unknown Literary Group',
      books: books.length,
      characters: 0,  // Would need to count from characterZone
      variations: 0   // Would need to count from variationZone
    });
  }, [litGroups, sharedData.books]);

  const getLitGroupDeleteImpact = getDeleteImpact; // Alias for compatibility

  // Track if we've attempted initial load to prevent infinite loop
  const initialLoadAttempted = useRef(false);

  // Auto-fetch lit groups on mount (only once)
  useEffect(() => {
    if (litGroups.length === 0 && !litGroupsLoading && !initialLoadAttempted.current) {
      console.log('[useLitGroupOperationsZoneBased] Auto-loading literary groups (initial load)');
      initialLoadAttempted.current = true;
      fetchLitGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [litGroups.length, litGroupsLoading]); // Intentionally omitting fetchLitGroups to prevent infinite loop

  // Return API maintaining backward compatibility
  return {
    // Data
    litGroups,
    selectedLitGroup,
    selectedLitGroupProfile,
    selectedLitGroupId,

    // States
    litGroupsLoading,
    litGroupsError,
    litGroupsLastUpdated,

    // Operations
    fetchLitGroups,
    fetchLitGroup,
    fetchLitGroupProfile: fetchLitGroup, // Alias
    selectLitGroup,
    setLitGroupSelection, // Backward compatibility alias
    createLitGroup,
    updateLitGroup,
    deleteLitGroup,
    getDeleteImpact,
    getLitGroupDeleteImpact, // Alias

    // Zone data access for advanced use
    sharedData
  };
};

// Export as default
export default useLitGroupOperationsZoneBased;

// Also export with old name for easier migration
export const useLitGroupOperations = useLitGroupOperationsZoneBased;
